import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { DoodleArrow, DoodleSparkle, DoodleUnderline } from "./Doodles";
import WaveField from "./Waves";
import { useLang } from "@/i18n";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const catY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const catRotate = useTransform(scrollYProgress, [0, 1], [0, -10]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" data-testid="hero-section" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
      <div className="absolute top-24 right-[8%] w-40 h-40 sm:w-64 sm:h-64 rounded-full bg-[#8B5CF6]/50 blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-16 left-[4%] w-48 h-48 rounded-full bg-[#F2C230]/60 blur-3xl" aria-hidden="true" />
      <WaveField variant="light" className="absolute -bottom-20 -left-16 w-[440px] sm:w-[600px] opacity-30 pointer-events-none" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center w-full">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono-label text-[11px] sm:text-xs tracking-[0.3em] uppercase text-[#2B0A4E]/60 mb-6"
            data-testid="hero-eyebrow"
          >
            {t("hero_eyebrow")}
          </motion.p>

          <h1 className="font-serif-display font-bold text-[#2B0A4E] text-4xl sm:text-5xl lg:text-6xl xl:text-[4.6rem] leading-[1.08] tracking-tight" data-testid="hero-headline">
            <MaskedLine delay={0.35}>{t("hero_l1")}</MaskedLine>
            <MaskedLine delay={0.5} className="italic text-[#8B5CF6]">{t("hero_l2")}</MaskedLine>
            <MaskedLine delay={0.65}>{t("hero_l3")}</MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="mt-7 max-w-xl text-base sm:text-lg text-[#5E4783] leading-relaxed" data-testid="hero-subcopy">
              {t("hero_sub")}
            </p>
            <div className="relative inline-block mt-2">
              <DoodleUnderline className="w-56 h-4" />
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-cta-primary"
                onClick={() => window.__lenis?.scrollTo("#plans", { offset: -72 })}
                className="sketch-border bg-[#2B0A4E] text-[#FDFBF5] px-7 py-3.5 font-mono-label text-xs tracking-[0.2em] uppercase hover:bg-[#3E116B] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
              >
                {t("hero_cta1")}
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => window.__lenis?.scrollTo("#contact", { offset: -72 })}
                className="sketch-border bg-transparent text-[#2B0A4E] px-7 py-3.5 font-mono-label text-xs tracking-[0.2em] uppercase hover:bg-[#8B5CF6]/50 hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
              >
                {t("hero_cta2")}
              </button>
            </div>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
          <motion.div
            style={{ y: catY, rotate: catRotate }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute inset-0 -m-8 rounded-full bg-[#F0E9FF]/70 blur-2xl" aria-hidden="true" />
            <img
              src="/assets/logo-full.png"
              alt="KATZYA cat logo"
              data-testid="hero-cat-logo"
              className="relative w-56 sm:w-72 lg:w-80 h-auto drop-shadow-[0_24px_40px_rgba(43,10,78,0.15)]"
              draggable="false"
            />
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-6 -right-8"
            >
              <DoodleSparkle className="w-9 h-9 text-[#2B0A4E]" />
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="absolute -bottom-4 left-2 sm:left-10 hidden sm:block"
          >
            <DoodleArrow className="w-24 h-16 -scale-x-100 rotate-12" />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#2B0A4E]/40"
      >
        {t("hero_scroll")}
      </motion.div>
    </section>
  );
}
